"use client";

import * as React from "react";
import { signIn } from "next-auth/react";
import { toast } from "sonner";

import { Icons } from "@/components/shared/icons";
import { buttonVariants } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface EmailCodeFormProps {
  email: string;
  callbackUrl: string;
  referralCode?: string | null;
  className?: string;
  onBack?: () => void;
}

export function EmailCodeForm({
  email,
  callbackUrl,
  referralCode = null,
  className,
  onBack,
}: EmailCodeFormProps) {
  const [code, setCode] = React.useState("");
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [isResending, setIsResending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isFocused, setIsFocused] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  async function onVerify(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError("Enter the 6-digit code from your email.");
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const res = await fetch("/api/auth/email-code/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: trimmed, callbackUrl }),
      });
      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(body.error || "Invalid code. Please try again.");
        setIsVerifying(false);
        return;
      }

      if (!body.url || typeof body.url !== "string") {
        setError("Something went wrong. Please try again.");
        setIsVerifying(false);
        return;
      }

      window.location.assign(body.url);
    } catch {
      setError("Something went wrong. Please try again.");
      setIsVerifying(false);
    }
  }

  async function onResend() {
    setIsResending(true);
    setError(null);

    const signInResult = await signIn("resend", {
      email,
      redirect: false,
      callbackUrl,
      ...(referralCode && { referralCode }),
    });

    setIsResending(false);

    if (!signInResult?.ok) {
      toast.error("Could not resend code. Please try again.");
      return;
    }

    setCode("");
    toast.success("Code resent", {
      description: "Check your email for a new code and link.",
    });
  }

  return (
    <div className={cn("grid gap-4", className)}>
      <div className="space-y-1 text-center">
        <p className="text-sm text-muted-foreground">
          We sent a sign-in code and link to
        </p>
        <p className="text-sm text-foreground">{email}</p>
      </div>

      <form onSubmit={onVerify} className="grid gap-2">
        <div className="grid gap-1">
          <Label className="sr-only" htmlFor="email-login-code">
            Sign-in code
          </Label>
          {/* One invisible real input over 6 visual boxes: keeps paste and
              iOS/Android one-time-code autofill working natively. */}
          <div className="relative">
            <input
              ref={inputRef}
              id="email-login-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                setError(null);
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onSelect={(e) => {
                // Keep the caret at the end so typing always fills the next box.
                const el = e.currentTarget;
                el.setSelectionRange(el.value.length, el.value.length);
              }}
              disabled={isVerifying || isResending}
              className="absolute inset-0 z-10 size-full cursor-pointer opacity-0"
              autoFocus
            />
            <div className="flex justify-center gap-2" aria-hidden="true">
              {Array.from({ length: 6 }).map((_, i) => {
                const isActive =
                  isFocused && i === Math.min(code.length, 5);
                return (
                  <div
                    key={i}
                    className={cn(
                      "flex h-12 w-10 items-center justify-center rounded-md border bg-background text-lg font-medium",
                      isActive
                        ? "border-emerald-500 ring-1 ring-emerald-500"
                        : "border-input",
                      (isVerifying || isResending) && "opacity-50"
                    )}
                  >
                    {code[i] ?? ""}
                  </div>
                );
              })}
            </div>
          </div>
          {error && <p className="px-1 text-xs text-red-600">{error}</p>}
        </div>

        <button
          type="submit"
          className={cn(
            buttonVariants(),
            "shrink-0 whitespace-nowrap bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:opacity-90"
          )}
          disabled={isVerifying || isResending || code.length !== 6}
        >
          {isVerifying && (
            <Icons.spinner className="mr-2 size-4 animate-spin" />
          )}
          Verify code
        </button>
      </form>

      <div className="flex flex-col items-center gap-2 text-xs text-muted-foreground">
        <p>Code expires in 5 minutes. Check spam if you do not see it.</p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onResend}
            disabled={isVerifying || isResending}
            className="underline underline-offset-4 hover:text-foreground disabled:opacity-50"
          >
            {isResending ? "Sending..." : "Resend code"}
          </button>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              disabled={isVerifying || isResending}
              className="underline underline-offset-4 hover:text-foreground disabled:opacity-50"
            >
              Use a different email
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
